import { type Item } from '@shared/board'
import { edgePath } from '@shared/whiteboard/edges'
import { clsx } from 'clsx'
import { memo, useEffect, useId, useRef, useState } from 'react'
import { useI18n } from '@/i18n/i18n'
import { CardText } from '@/board/items/CardText'
import { FileView } from '@/board/items/FileView'
import { ImageView } from '@/board/items/ImageView'
import { LinkView } from '@/board/items/LinkView'
import { SessionNodeView } from '@/board/items/SessionNodeView'
import { ClipView } from '@/board/items/ClipView'
import { MaterialView } from '@/board/items/MaterialView'
import { type Scene } from './embeds'

// Part of the board drawn small and read-only inside a message: every thing at
// its board position, scaled to fit the message's width (never enlarged), with
// the connections between them. Live: it follows the board as it changes.

function Thing({ item }: { item: Item }) {
  const { t } = useI18n()
  switch (item.kind) {
    case 'card':
      return (
        <div className="size-full overflow-hidden rounded-md border border-line bg-surface shadow-sm">
          <CardText id={item.id} className="px-3.5 pt-3 pb-3" />
        </div>
      )
    case 'message': {
      const fromAi = item.data.author.kind === 'session'
      return (
        <div
          className={clsx(
            'rounded-[4px_14px_14px_14px] border px-3 py-2 text-[13.5px] leading-relaxed whitespace-pre-wrap text-ink shadow-sm [overflow-wrap:anywhere]',
            fromAi ? 'border-line bg-surface' : 'border-(--note-line) bg-(--note)',
          )}
        >
          {item.data.text}
        </div>
      )
    }
    case 'group':
      return (
        <div className="relative size-full">
          <div className="absolute inset-0 rounded-xl border-[1.5px] border-line-strong bg-(--group-fill)" />
          <div className="absolute -top-3.5 left-3 max-w-[calc(100%-1.5rem)] truncate rounded-sm bg-canvas px-2 py-0.5 text-[12.5px] font-semibold tracking-[0.02em] text-ink-2">
            {item.data.title || t('group.untitled')}
          </div>
        </div>
      )
    case 'image':
      return <ImageView item={item} selected={false} />
    case 'file':
      return <FileView item={item} selected={false} />
    case 'link':
      return <LinkView item={item} selected={false} />
    case 'session':
      return <SessionNodeView item={item} selected={false} />
    case 'clip':
      return <ClipView item={item} selected={false} />
    case 'material':
      return <MaterialView item={item} selected={false} />
  }
}

export const SceneView = memo(function SceneView({
  scene,
  maxHeight,
  onPick,
}: {
  scene: Scene
  /** Tallest it may be, in screen pixels; it shrinks to fit. */
  maxHeight: number
  /** A thing in the scene was clicked. */
  onPick?: (id: string) => void
}) {
  const box = useRef<HTMLDivElement>(null)
  // React ids contain ":" (React 18) or "«»" (19): fine in HTML, not in url(#…).
  const arrow = `scene-arrow-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = box.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => {
      if (e) setWidth(e.contentRect.width)
    })
    ro.observe(el)
    return () => {
      ro.disconnect()
    }
  }, [])

  const { frame, items, edges } = scene
  const scale = width ? Math.min(1, width / frame.w, maxHeight / frame.h) : 0
  const byId = new Map(items.map((i) => [i.id, i]))
  // Groups behind everything, then the board's own stacking order.
  const ordered = [...items].sort(
    (a, b) => Number(b.kind === 'group') - Number(a.kind === 'group') || a.z - b.z,
  )
  return (
    <div
      ref={box}
      className="moot-canvas moot-scene relative w-full overflow-hidden"
      style={{ height: scale ? frame.h * scale : maxHeight / 2 }}
    >
      {scale > 0 && (
        <div
          className="absolute top-0 left-1/2 origin-top-left"
          style={{
            width: frame.w,
            height: frame.h,
            transform: `translateX(${(-frame.w * scale) / 2}px) scale(${scale})`,
          }}
        >
          <svg
            className="pointer-events-none absolute inset-0 overflow-visible"
            width={frame.w}
            height={frame.h}
            aria-hidden
          >
            <defs>
              <marker
                id={arrow}
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="7"
                markerHeight="7"
                orient="auto-start-reverse"
              >
                <path d="M0,0 L10,5 L0,10 z" fill="var(--edge)" />
              </marker>
            </defs>
            {edges.map((e) => {
              const a = byId.get(e.from)
              const b = byId.get(e.to)
              if (!a || !b) return null
              const local = (i: Item) => ({ x: i.x - frame.x, y: i.y - frame.y, w: i.w, h: i.h })
              const { d } = edgePath(local(a), local(b))
              return (
                <path
                  key={e.id}
                  d={d}
                  fill="none"
                  stroke="var(--edge)"
                  strokeWidth={1.5}
                  vectorEffect="non-scaling-stroke"
                  markerEnd={`url(#${arrow})`}
                />
              )
            })}
          </svg>
          {ordered.map((i) => (
            <div
              key={i.id}
              className={clsx('absolute', onPick && i.kind !== 'group' && 'cursor-pointer')}
              style={{
                left: i.x - frame.x,
                top: i.y - frame.y,
                width: i.w,
                height: i.kind === 'message' || i.kind === 'link' ? undefined : i.h,
              }}
              onClick={
                onPick && i.kind !== 'group'
                  ? () => {
                      onPick(i.id)
                    }
                  : undefined
              }
            >
              <Thing item={i} />
            </div>
          ))}
          {edges.map((e) => {
            const a = byId.get(e.from)
            const b = byId.get(e.to)
            if (!a || !b || !e.label) return null
            const local = (i: Item) => ({ x: i.x - frame.x, y: i.y - frame.y, w: i.w, h: i.h })
            const { mid } = edgePath(local(a), local(b))
            return (
              <span
                key={e.id}
                className="absolute -translate-x-1/2 -translate-y-1/2 rounded-sm bg-canvas px-1.5 py-px text-[12px] whitespace-nowrap text-muted"
                style={{ left: mid.x, top: mid.y }}
              >
                {e.label}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
})
